/**
 * How old is too old to be worth a rep's attention on My Pipeline.
 *
 * The six-week sweep before go-live closed ten thousand never-decided quotes,
 * but a residue survives: quotes that were touched recently enough to escape
 * the sweep, then went quiet. Every one of them is already past the end of its
 * age curve — `written_off`, carrying nothing in the forecast. They are untidy
 * rather than wrong, and three reps independently asked to stop seeing them in
 * week one.
 *
 * So this is presentation, not arithmetic. Hiding a quote here changes what a
 * rep reads; it never changes what the forecast counts. The quote is still in
 * the response, still in the client's totals, and one toggle away.
 *
 * Age is counted in working days because that is what the book is priced in
 * (`age_wd` on the quote, the same figure the win-rate curve is read at), so
 * six months is six working months, not 183 calendar days.
 *
 * **The threshold moved once already.** It shipped at a year, because the
 * original ask said "year-old estimates". That hid 73 quotes worth £96k and
 * left the 241 between three and twelve months — £514k, almost all of them
 * equally written off — still on the page, and reps said so within the week.
 * Six months was then chosen by the business off the age profile below, not
 * derived from the curve. It is a presentation preference and may move again;
 * what must not happen is it moving silently, so the number lives here with
 * its reasoning rather than inline at the call sites.
 *
 * Live book at the time of the decision (LIVE era, open + stale):
 *
 * | Age            | Quotes | Value |
 * | -------------- | -----: | ----: |
 * | under 3 months |  2,245 | £2.18m |
 * | 3–6 months     |     69 | £141k |
 * | 6–12 months    |    172 | £373k |
 * | over a year    |     73 |  £96k |
 */
import type { PipelineQuote } from "../data/my-pipeline-types";

/**
 * Working days in six months — half of 250, a working year being 52 weeks less
 * the usual bank holidays.
 */
export const WORKING_DAYS_IN_SIX_MONTHS = 125;

/**
 * A quote old enough to hide by default.
 *
 * Named for both halves on purpose. There is no new domain noun here — no
 * "dormant" or "dead" state gets minted by a filter — just the two facts the
 * quote already carries, stated together. The name carries the threshold so
 * that changing the number cannot leave a call site reading as if it had not
 * changed.
 *
 * Both halves are required, and the conjunction is the point. Age alone would
 * hide a live quote on a long procurement cycle, which is precisely the work a
 * rep must not lose sight of; `written_off` alone is already visible as the
 * "Carrying nothing" badge and can be raised inside a month on a fast bucket,
 * which is far too soon to take a quote off the page. Only the pair — past its
 * curve AND past six months — is reliably dead.
 */
export function isOverSixMonthsAndWrittenOff(quote: PipelineQuote): boolean {
  return (
    quote.age_wd > WORKING_DAYS_IN_SIX_MONTHS &&
    quote.flags.includes("written_off")
  );
}
